import type { Settings as LayoutSettings, MenuDataItem } from '@ant-design/pro-components';
import type { RunTimeLayoutConfig } from '@umijs/max';
import React from 'react';
import '@ant-design/v5-patch-for-react-19';
import { AvatarDropdown, SelectLang, ThemeSwitch } from '@/components';
import { MobileTabBar } from '@/components/MobileTabBar';
import { BrandMark } from '@/components/BrandMark';
import AuthBoundary from '@/components/AuthBoundary';
import defaultSettings from '../config/defaultSettings';
import { loadAuthState } from '@/utils/authStorage';
import { ThemeModeProvider } from '@/hooks/useThemeMode';

export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: {
    id: number;
    username: string;
    email: string | null;
    fullName: string;
    globalRole: string;
    isActive: boolean;
  };
}> {
  const persisted = loadAuthState();
  return {
    settings: defaultSettings as Partial<LayoutSettings>,
    currentUser: persisted?.user,
  };
}

const filterMenuForStandardUser = (menuData: MenuDataItem[]): MenuDataItem[] =>
  menuData
    .map((item) => {
      if (!item || !item.path) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        return {
          ...item,
          children: filterMenuForStandardUser(item.children as MenuDataItem[]),
        };
      }
      // 普通用户只保留「我的平台」。
      // 以前这里留的是 /dashboard 并把它改名成「我的平台」—— 那是拆页面之前的写法，
      // 现在「我的平台」是独立路由，再指向 /dashboard 就会把人送到系统概览。
      if (item.path === '/my-platforms') {
        return item;
      }
      return null;
    })
    .filter((item): item is MenuDataItem => Boolean(item));

export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  const globalRole = initialState?.currentUser?.globalRole ?? null;
  const isStandardUser = globalRole === 'USER';

  return {
    avatarProps: {
      render: () => <AvatarDropdown />,
    },
    actionsRender: () => [<ThemeSwitch key="theme" />, <SelectLang key="lang" />],
    // 顶栏和登录页用同一个品牌标识（带认证角标、Auth 是蓝的）。
    // mix 布局的品牌位在顶栏（headerTitleRender）；之前挂在 menuHeaderRender 上，
    // 结果落到了侧栏头部，顶栏又用 logo+title 默认画了一个，页面上出现两个牌子。
    headerTitleRender: () => <BrandMark inline />,
    // menuHeaderRender 只为手机保留：手机顶栏（调用时不带 props）和抽屉菜单
    // （isMobile=true）用它画品牌；桌面侧栏（isMobile=false）返回 false 不画，
    // 和 mix 布局的默认行为一致，免得和顶栏重复。
    menuHeaderRender: (_logo, _title, props) =>
      props?.isMobile === false ? false : <BrandMark inline />,
    // 手机端底部导航。挂在 childrenRender 里，所有走布局的页面都有。
    childrenRender: (children) => (
      <>
        {children}
        <MobileTabBar />
      </>
    ),
    footerRender: () => null,
    layout: 'mix',
    siderWidth: 220,
    fixedHeader: true,
    splitMenus: false,
    menuDataRender: (menuData) =>
      isStandardUser ? filterMenuForStandardUser(menuData || []) : menuData,
    ...initialState?.settings,
  };
};

export const rootContainer = (container: React.ReactNode) => (
  <ThemeModeProvider>
    {container}
  </ThemeModeProvider>
);

// 放在 Umi 数据流 Provider 内，认证边界才能同步菜单和路由所用的 initialState。
export const innerProvider = (container: React.ReactNode) => (
  <AuthBoundary>{container}</AuthBoundary>
);
